import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import teamService from "../../services/api/teamService";
import tournamentService from "../../services/api/tournamentService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import EntityCard from "../ui/EntityCard";
import { membersLine, onCourtSize } from "../../utils/teamCapacity";
import ConfirmModal from "../ui/ConfirmModal";
import TeamBrowser from "./TeamBrowser";
import TeamCreator from "./TeamCreator";
import {
  TEAM_MEMBER_ADMIN_ROLES,
  TEAM_MEMBER_ROLE_PRIORITY,
  TEAM_MEMBER_ROLES,
  USER_ROLES,
  normalizeUserRole,
} from "../../constants/roles";
import { getSportEmoji } from "../../utils/sportIcons";
import "../../styles/features/teams.css";
import mediaUrl from "../../utils/mediaUrl";

const TeamManagement = ({ currentUser, token, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Derive current view from URL pathname (source of truth)
  const getCurrentViewFromPath = () => {
    const path = location.pathname;
    if (path.includes('/my-teams')) return 'my-teams';
    if (path.includes('/browse')) return 'browse';
    if (path.includes('/create')) return 'create';
    // Default to 'browse' for /teams root path
    return 'browse';
  };
  
  const currentView = getCurrentViewFromPath();
  const pendingSectionRef = useRef(null);
  
  const [userTeams, setUserTeams] = useState([]);
  const [invitations, setInvitations] = useState([]); // Incoming: Teams invited user
  const [incomingRequests, setIncomingRequests] = useState([]); // Incoming: Users requesting to join user's teams
  const [tournamentTeamInvites, setTournamentTeamInvites] = useState([]); // Tournament/league invitations sent to user's teams
  const [respondingTournamentInvite, setRespondingTournamentInvite] = useState(null);
  const [respondingInvitation, setRespondingInvitation] = useState(null); // tracks which team invite is in-flight
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const normalizedUserRole = normalizeUserRole(currentUser?.role);
  const isSuperAdmin = normalizedUserRole === USER_ROLES.SUPER_ADMIN;

  const scrollToPendingSection = useCallback(() => {
    if (pendingSectionRef.current) {
      pendingSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useEffect(() => {
    if (currentView === "my-teams" && location.state?.focusPending) {
      const timer = setTimeout(() => {
        scrollToPendingSection();
      }, 120);

      return () => clearTimeout(timer);
    }
  }, [currentView, location.state?.focusPending, scrollToPendingSection]);

  const fetchUserTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch user's active teams only (exclude inactive/left teams)
      const teams = await teamService.getUserTeams({ status: 'active' });
      
      // Handle both array and object with data property
      let memberTeams = [];
      if (Array.isArray(teams)) {
        memberTeams = teams;
      } else if (teams?.data?.teams) {
        memberTeams = Array.isArray(teams.data.teams) ? teams.data.teams : [];
      } else if (teams?.teams) {
        memberTeams = Array.isArray(teams.teams) ? teams.teams : [];
      }

      // Also fetch teams created by the user (in case they're not in the membership table)
      let createdTeams = [];
      // NOTE: The backend /teams/my-teams endpoint now includes creator's teams via a UNION
      // query, so a separate limit:1000 call is no longer needed here.
      createdTeams = memberTeams.filter(t => t.created_by === currentUser.id || t.is_creator);

      // Merge teams (avoid duplicates)
      const teamIds = new Set(memberTeams.map(t => t.team_id || t.id));
      const allUserTeams = [...memberTeams];
      
      createdTeams.forEach(team => {
        const teamId = team.team_id || team.id;
        if (!teamIds.has(teamId)) {
          // Mark as creator for display
          allUserTeams.push({
            ...team,
            member_role: 'creator',
            is_creator: true
          });
        } else {
          // Update existing team to mark as creator
          const existingTeam = allUserTeams.find(t => (t.team_id || t.id) === teamId);
          if (existingTeam && existingTeam.created_by === currentUser.id) {
            existingTeam.is_creator = true;
          }
        }
      });

      // Sort: Creator teams first, then by role, then by name
      allUserTeams.sort((a, b) => {
        // Creator teams first
        const aIsCreator = a.created_by === currentUser.id || a.is_creator;
        const bIsCreator = b.created_by === currentUser.id || b.is_creator;
        if (aIsCreator && !bIsCreator) return -1;
        if (!aIsCreator && bIsCreator) return 1;
        
        // Then by role priority (owner/admin > manager > member)
        const aPriority = TEAM_MEMBER_ROLE_PRIORITY[a.member_role] || 99;
        const bPriority = TEAM_MEMBER_ROLE_PRIORITY[b.member_role] || 99;
        if (aPriority !== bPriority) return aPriority - bPriority;
        
        // Finally alphabetically by name
        return (a.team_name || a.name || '').localeCompare(b.team_name || b.name || '');
      });

      setUserTeams(allUserTeams);

      setError("");
    } catch (err) {
      console.error("Error fetching user teams:", err);
      
      // Only show error if it's not a 404 (which means no teams)
      if (err.response?.status === 404 || err.response?.status === 200) {
        setUserTeams([]);
        setError("");
      } else {
        setError(`Failed to load your teams: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchPendingRequests = useCallback(async () => {
    try {
      // Fetch pending invitations (incoming - teams invited user)
      try {
        const invites = await teamService.getUserInvitations('pending');
        const invitesList = Array.isArray(invites) 
          ? invites 
          : (invites?.data?.invitations || invites?.data || []);
        setInvitations(invitesList);
      } catch (invErr) {
        console.error('Error fetching invitations:', invErr);
        setInvitations([]);
      }

      // Fetch incoming join requests (users requesting to join teams the current user owns)
      try {
        const teams = await teamService.getUserTeams({ status: 'active' });
        
        // Handle different response formats
        let teamsList = [];
        if (Array.isArray(teams)) {
          teamsList = teams;
        } else if (teams?.data) {
          if (Array.isArray(teams.data)) {
            teamsList = teams.data;
          } else if (teams.data.teams && Array.isArray(teams.data.teams)) {
            teamsList = teams.data.teams;
          } else if (teams.data.userTeams && Array.isArray(teams.data.userTeams)) {
            teamsList = teams.data.userTeams;
          }
        }
        
        // Also fetch ALL teams and filter by created_by to catch teams where
        // user is the creator but doesn't have a membership record.
        // NOTE: The backend /teams/my-teams now handles this via UNION — no
        // separate limit:1000 call needed. teamsList already includes creator teams.
        // (The block below is intentionally removed)
        
        // Get join requests for each team the user owns/manages
        const allIncomingRequests = [];
        for (const team of teamsList) {
          const teamId = team.team_id || team.id;
          const memberRole = team.member_role?.toLowerCase();
          const isOwner = team.created_by === currentUser.id || isSuperAdmin || TEAM_MEMBER_ADMIN_ROLES.includes(memberRole);
          
          if (isOwner) {
            try {
              const teamRequests = await teamService.getJoinRequests(teamId);
              const requestsList = Array.isArray(teamRequests) ? teamRequests : [];
              // Add team info to each request
              requestsList.forEach(req => {
                allIncomingRequests.push({
                  ...req,
                  team_id: teamId,
                  team_name: team.team_name || team.name,
                  team_logo: team.logo_url || team.logo
                });
              });
            } catch (teamReqErr) {
              console.error(`Error fetching requests for team ${teamId}:`, teamReqErr);
            }
          }
        }
        setIncomingRequests(allIncomingRequests);
      } catch (teamsErr) {
        console.error('Error fetching incoming requests:', teamsErr);
        setIncomingRequests([]);
      }

      // Fetch pending tournament/league invitations for teams the user owns
      try {
        const invRes = await tournamentService.getMyTeamInvitations("pending");
        const invList = Array.isArray(invRes?.data) ? invRes.data : [];
        setTournamentTeamInvites(invList);
      } catch (tInvErr) {
        console.error('Error fetching tournament team invitations:', tInvErr);
        setTournamentTeamInvites([]);
      }
    } catch (err) {
      console.error("Error fetching pending requests:", err);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      // Load teams for both my-teams and browse views
      fetchUserTeams();
      // Always fetch pending requests to show notification badge in both views
      fetchPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentUser?.id]); // Only depend on pathname and user ID, not the functions

  const handleTeamSelect = (teamId, options = {}) => {
    // Navigate to team details, passing current view in state for back button
    navigate(`/teams/${teamId}`, {
      state: {
        returnTo: currentView,
        returnPath: location.pathname,
        returnState: location.state,
        ...options,
      }
    });
  };

  const handleAcceptInvitation = async (invitationId) => {
    if (respondingInvitation) return; // prevent double-click
    setRespondingInvitation(invitationId);
    // Optimistic removal so the badge updates immediately
    setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    try {
      await teamService.acceptInvitation(invitationId);
      await Promise.all([fetchUserTeams(), fetchPendingRequests()]);
      setError("");
    } catch (err) {
      console.error("Error accepting invitation:", err);
      // Roll back optimistic removal on error
      await fetchPendingRequests();
      setError("Failed to accept invitation. Please try again.");
    } finally {
      setRespondingInvitation(null);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    if (respondingInvitation) return;
    setRespondingInvitation(invitationId);
    // Optimistic removal
    setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    try {
      await teamService.declineInvitation(invitationId);
      await fetchPendingRequests();
      setError("");
    } catch (err) {
      console.error("Error declining invitation:", err);
      await fetchPendingRequests();
      setError("Failed to decline invitation. Please try again.");
    } finally {
      setRespondingInvitation(null);
    }
  };

  const handleApproveJoinRequest = async (teamId, userId, userName) => {
    try {
      await teamService.approveJoinRequest(teamId, userId);
      // Refresh pending requests to update the UI
      await fetchPendingRequests();
      setError(""); // Clear any existing errors
    } catch (err) {
      console.error("Error approving join request:", err);
      setError(`Failed to approve ${userName}'s request. Please try again.`);
    }
  };

  const handleRejectJoinRequest = async (teamId, userId, userName) => {
    try {
      await teamService.rejectJoinRequest(teamId, userId);
      // Refresh pending requests to update the UI
      await fetchPendingRequests();
      setError(""); // Clear any existing errors
    } catch (err) {
      console.error("Error rejecting join request:", err);
      setError(`Failed to reject ${userName}'s request. Please try again.`);
    }
  };

  const handleEditTeam = (teamId) => {
    // Navigate to edit team page
    navigate(`/teams/${teamId}/edit`);
  };

  const handleDeleteTeam = (team) => {
    if (!currentUser) {
      setError("You must be logged in to delete this team.");
      return;
    }
    setTeamToDelete(team);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      setIsDeleting(true);
      setError("");
      // My Teams rows carry `id`; membership rows carry `team_id`
      await teamService.deleteTeam(teamToDelete.team_id ?? teamToDelete.id);
      
      // Refresh user teams after successful deletion
      await fetchUserTeams();
      
      // Close modal and reset
      setShowDeleteConfirm(false);
      setTeamToDelete(null);
    } catch (err) {
      console.error("Error deleting team:", err);
      setError(`Failed to delete team "${teamToDelete.name}". Please try again.`);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteTeam = () => {
    setShowDeleteConfirm(false);
    setTeamToDelete(null);
  };

  if (currentView === "my-teams") {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-management-gaming">
          {/* Compact Teams Navigation Header */}
          <div className="teams-header-compact">
            <FloatingCard className="header-card-compact delay-1">
              <div className="teams-nav-compact">
                <div className="teams-nav-left-compact">
                  <h2 className="teams-title-compact">👥 My Teams</h2>
                  <p className="teams-subtitle-compact">
                    Teams you created and teams you're a member of
                  </p>
                </div>
                <div className="teams-nav-right-compact">
                  {/* Pending Requests Notification Bell */}
                  {(() => {
                    const totalPending = invitations.length + incomingRequests.length + tournamentTeamInvites.length;
                    return totalPending > 0 && (
                      <div className="pending-notification-badge" data-count={totalPending}>
                        <FloatingButton
                          variant="warning"
                          size="small"
                          icon="🔔"
                          title={`${totalPending} pending item${totalPending !== 1 ? 's' : ''} — click to review`}
                          onClick={scrollToPendingSection}
                        >
                          <span className="btn-text">Pending ({totalPending})</span>
                        </FloatingButton>
                      </div>
                    );
                  })()}
                  
                  <FloatingButton
                    variant="secondary"
                    size="small"
                    onClick={() => navigate('/teams/browse')}
                    icon="🔍"
                  >
                    <span className="btn-text">Browse Teams</span>
                  </FloatingButton>
                  <FloatingButton
                    variant="primary"
                    size="small"
                    onClick={() => navigate('/teams/create')}
                    icon="➕"
                  >
                    <span className="btn-text">Create Team</span>
                  </FloatingButton>
                </div>
              </div>
            </FloatingCard>
          </div>

          {error && (
            <FloatingCard className="alert-card alert-error-gaming">
              <div className="alert-content">
                <span className="alert-icon">⚠️</span>
                <div className="alert-message">{error}</div>
              </div>
            </FloatingCard>
          )}

          {loading ? (
            <FloatingCard className="loading-card-compact">
              <div className="loading-content-gaming">
                <div className="gaming-spinner"></div>
                <p className="loading-text">Loading your teams...</p>
              </div>
            </FloatingCard>
          ) : (
            <div className="my-teams-content-compact">
              {/* Two Column Layout */}
              <div className="teams-columns-layout">
                {/* Left Column - User's Teams */}
                <div className="teams-column" ref={pendingSectionRef}>
                  <FloatingCard className="section-card-compact delay-2">
                    <h3 className="section-title-compact">
                      <span className="section-icon">👥</span>
                      Your Teams ({userTeams.length})
                    </h3>

                    {userTeams.length > 0 ? (
                      <div className="teams-grid-sports-style">
                        {userTeams.map((team) => {
                          const teamId = team.team_id || team.id;
                          const activeCount = team.active_members_count || 0;
                          const isCreator = team.created_by === currentUser.id || team.is_creator;
                          const role = team.member_role?.toLowerCase();
                          let roleBadge = { label: "👤 Member", tone: "neutral" };
                          if (isCreator) {
                            roleBadge = { label: "👑 Creator", tone: "warning" };
                          } else if ([TEAM_MEMBER_ROLES.OWNER, TEAM_MEMBER_ROLES.ADMIN].includes(role)) {
                            roleBadge = { label: "💼 Admin", tone: "warning" };
                          } else if ([TEAM_MEMBER_ROLES.MANAGER, TEAM_MEMBER_ROLES.COACH].includes(role)) {
                            roleBadge = { label: "📋 Manager", tone: "warning" };
                          }
                          const canEdit = isCreator || isSuperAdmin || TEAM_MEMBER_ADMIN_ROLES.includes(role);
                          const openMembers = (event) => {
                            event.stopPropagation();
                            handleTeamSelect(teamId, { initialTab: "members" });
                          };
                          return (
                            <EntityCard
                              key={teamId}
                              title={team.team_name || team.name}
                              logoUrl={team.logo_url || team.logo}
                              badges={[
                                { label: team.sport || team.sport_type || "Multi-Sport", tone: "sport" },
                                roleBadge,
                              ]}
                              rows={[
                                {
                                  key: "members",
                                  icon: "👥",
                                  label: "Members",
                                  value: (
                                    <span
                                      className="entity-card__link"
                                      role="button"
                                      tabIndex={0}
                                      onClick={openMembers}
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                          event.preventDefault();
                                          openMembers(event);
                                        }
                                      }}
                                      aria-label="View team members"
                                    >
                                      {membersLine(activeCount, onCourtSize(team))}
                                    </span>
                                  ),
                                },
                                {
                                  key: "joined",
                                  icon: "📅",
                                  label: "Joined",
                                  value: team.joined_date ? new Date(team.joined_date).toLocaleDateString() : "N/A",
                                },
                                team.member_role && {
                                  key: "role",
                                  icon: "🎭",
                                  label: "Role",
                                  value: team.member_role.charAt(0).toUpperCase() + team.member_role.slice(1),
                                },
                              ].filter(Boolean)}
                              actions={canEdit && (
                                <>
                                  <FloatingButton
                                    variant="outline"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditTeam(teamId);
                                    }}
                                    icon="✏️"
                                  >
                                    <span className="btn-text">Edit</span>
                                  </FloatingButton>
                                  <FloatingButton
                                    variant="danger"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTeam(team);
                                    }}
                                    icon="🗑️"
                                  >
                                    <span className="btn-text">Delete</span>
                                  </FloatingButton>
                                </>
                              )}
                              onClick={() => handleTeamSelect(teamId)}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="empty-state-compact">
                        <span className="empty-icon">👥</span>
                        <p className="empty-text">
                          No teams yet. Browse available teams to get started!
                        </p>
                        <button
                          className="btn-compact browse-btn"
                          onClick={() => navigate('/teams/browse')}
                        >
                          🔍 Browse Teams
                        </button>
                      </div>
                    )}
                  </FloatingCard>
                </div>

                {/* Right Column - Pending Requests & Invitations */}
                <div className="teams-column">
                  {/* Incoming Join Requests (Users requesting to join your teams) */}
                  {incomingRequests.length > 0 && (
                    <FloatingCard className="section-card-compact delay-3" style={{ marginBottom: '1rem' }}>
                      <h3 className="section-title-compact">
                        <span className="section-icon">📥</span>
                        Join Requests to Your Teams ({incomingRequests.length})
                      </h3>

                      <div className="requests-list-compact">
                        {incomingRequests.map((request) => (
                          <div
                            key={`${request.team_id}-${request.user_id}`}
                            className="request-card-compact incoming-request"
                          >
                            <div className="request-info-compact">
                              <div className="team-logo-small-compact">
                                {request.avatar_url ? (
                                  <img
                                    src={request.avatar_url}
                                    alt={`${request.first_name} ${request.last_name}`}
                                    className="request-logo-img"
                                  />
                                ) : (
                                  <div className="default-logo-compact">
                                    <span className="logo-icon">👤</span>
                                  </div>
                                )}
                              </div>
                              <div className="request-details-compact">
                                <h4 className="team-name-compact">
                                  {request.first_name} {request.last_name}
                                </h4>
                                <div className="request-meta-compact">
                                  <span className="sport-tag-compact">
                                    <span className="sport-icon">🏆</span>
                                    {request.team_name}
                                  </span>
                                  <span className="request-date-compact">
                                    {new Date(request.requested_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="request-actions-compact">
                              <button
                                className="btn-compact accept-btn"
                                onClick={() => handleApproveJoinRequest(request.team_id, request.user_id, request.first_name)}
                              >
                                ✅ Approve
                              </button>
                              <button
                                className="btn-compact decline-btn"
                                onClick={() => handleRejectJoinRequest(request.team_id, request.user_id, request.first_name)}
                              >
                                ❌ Reject
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </FloatingCard>
                  )}

                  {/* Incoming Team Invitations */}
                  {invitations.length > 0 && (
                    <FloatingCard className="section-card-compact delay-4">
                      <h3 className="section-title-compact">
                        <span className="section-icon">📨</span>
                        Team Invitations ({invitations.length})
                      </h3>

                      <div className="requests-list-compact">
                        {invitations.map((invitation) => (
                          <div
                            key={invitation.id}
                            className="request-card-compact invitation-card"
                          >
                            <div className="request-info-compact">
                              <div className="team-logo-small-compact">
                                {invitation.logo_url ? (
                                  <img
                                    src={mediaUrl(invitation.logo_url)}
                                    alt={`${invitation.team_name} logo`}
                                    className="request-logo-img"
                                  />
                                ) : (
                                  <div className="default-logo-compact">
                                    <span className="logo-icon">🏆</span>
                                  </div>
                                )}
                              </div>
                              <div className="request-details-compact">
                                <h4 className="team-name-compact">
                                  {invitation.team_name}
                                </h4>
                                <div className="request-meta-compact">
                                  <span className="sport-tag-compact">
                                    <span className="sport-icon">{getSportEmoji(invitation.sport_name || invitation.sport_type)}</span>
                                    {invitation.sport_type}
                                  </span>
                                  <span className="request-date-compact">
                                    Invited by @{invitation.inviter_username}
                                  </span>
                                </div>
                                {invitation.message && (
                                  <p className="invitation-message">
                                    "{invitation.message}"
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="request-actions-compact">
                              <button
                                className="btn-compact accept-btn"
                                disabled={respondingInvitation === invitation.id}
                                onClick={() =>
                                  handleAcceptInvitation(invitation.id)
                                }
                              >
                                {respondingInvitation === invitation.id ? "…" : "✅ Accept"}
                              </button>
                              <button
                                className="btn-compact decline-btn"
                                disabled={respondingInvitation === invitation.id}
                                onClick={() =>
                                  handleDeclineInvitation(invitation.id)
                                }
                              >
                                {respondingInvitation === invitation.id ? "…" : "❌ Decline"}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </FloatingCard>
                  )}

                  {/* Tournament / League Invitations for owned teams */}
                  {tournamentTeamInvites.length > 0 && (
                    <FloatingCard className="section-card-compact delay-5">
                      <h3 className="section-title-compact">
                        <span className="section-icon">🏆</span>
                        Tournament &amp; League Invitations ({tournamentTeamInvites.length})
                      </h3>
                      <div className="requests-list-compact">
                        {tournamentTeamInvites.map((inv) => {
                          const contextName = inv.tournament_name || inv.league_name || "Tournament";
                          const contextSport = inv.tournament_sport || inv.league_sport;
                          const isTournament = !!inv.tournament_id;
                          const teamName = inv.team_name || `Team #${inv.team_id}`;
                          return (
                            <div key={inv.id} className="request-card-compact invitation-card">
                              <div className="request-info-compact">
                                <div className="team-logo-small-compact">
                                  <div className="default-logo-compact">
                                    <span className="logo-icon">{isTournament ? "🏆" : "🏅"}</span>
                                  </div>
                                </div>
                                <div className="request-details-compact">
                                  <h4 className="team-name-compact">{contextName}</h4>
                                  <div className="request-meta-compact">
                                    {contextSport && (
                                      <span className="sport-tag-compact">
                                        <span className="sport-icon">{getSportEmoji(contextSport)}</span>
                                        {contextSport}
                                      </span>
                                    )}
                                    <span className="request-date-compact">
                                      Team: {teamName}
                                    </span>
                                  </div>
                                  <div className="request-meta-compact">
                                    <span className="request-date-compact">
                                      By {inv.invited_by_name || inv.invited_by_username || "Organizer"}
                                    </span>
                                    <span className="request-date-compact">
                                      {new Date(inv.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {inv.message && (
                                    <p className="invitation-message">"{inv.message}"</p>
                                  )}
                                </div>
                              </div>
                              <div className="request-actions-compact">
                                <button
                                  className="btn-compact accept-btn"
                                  disabled={respondingTournamentInvite === inv.id}
                                  onClick={async () => {
                                    setRespondingTournamentInvite(inv.id);
                                    try {
                                      await tournamentService.respondToTeamInvitation(inv.id, "accept");
                                      setTournamentTeamInvites((prev) => prev.filter((i) => i.id !== inv.id));
                                    } catch (err) {
                                      setError(err.message || "Failed to accept invitation");
                                    } finally {
                                      setRespondingTournamentInvite(null);
                                    }
                                  }}
                                >
                                  {respondingTournamentInvite === inv.id ? "…" : "✅ Accept"}
                                </button>
                                <button
                                  className="btn-compact decline-btn"
                                  disabled={respondingTournamentInvite === inv.id}
                                  onClick={async () => {
                                    setRespondingTournamentInvite(inv.id);
                                    try {
                                      await tournamentService.respondToTeamInvitation(inv.id, "decline");
                                      setTournamentTeamInvites((prev) => prev.filter((i) => i.id !== inv.id));
                                    } catch (err) {
                                      setError(err.message || "Failed to decline invitation");
                                    } finally {
                                      setRespondingTournamentInvite(null);
                                    }
                                  }}
                                >
                                  {respondingTournamentInvite === inv.id ? "…" : "❌ Decline"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </FloatingCard>
                  )}

                  {/* Empty State */}
                  {invitations.length === 0 && incomingRequests.length === 0 && tournamentTeamInvites.length === 0 && (
                    <FloatingCard className="section-card-compact delay-3">
                      <h3 className="section-title-compact">
                        <span className="section-icon">⏳</span>
                        No Pending Items
                      </h3>
                      <div className="empty-state-compact">
                        <span className="empty-icon">✅</span>
                        <p className="empty-text">All caught up!</p>
                      </div>
                    </FloatingCard>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteConfirm}
          title="Delete Team?"
          message={teamToDelete ? `Are you sure you want to delete "${teamToDelete.name}"? This action cannot be undone and will remove all team data, members, and statistics.` : ""}
          confirmText="Delete Team"
          cancelText="Cancel"
          onConfirm={confirmDeleteTeam}
          onCancel={cancelDeleteTeam}
          isLoading={isDeleting}
          isDanger={true}
        />
      </GameLayout>
    );
  }

  // Create team view
  if (currentView === "create") {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <div className="team-management-gaming">
          {/* Compact Teams Navigation Header */}
          <div className="teams-header-compact">
            <FloatingCard className="header-card-compact delay-1">
              <div className="teams-nav-compact">
                <div className="teams-nav-left-compact">
                  <h2 className="teams-title-compact">➕ Create Team</h2>
                  <p className="teams-subtitle-compact">
                    Start your own team and invite players
                  </p>
                </div>
                <div className="teams-nav-right-compact">
                  {/* Pending Requests Notification Bell */}
                  {currentUser && (() => {
                    const totalPending = invitations.length + incomingRequests.length + tournamentTeamInvites.length;
                    return totalPending > 0 && (
                      <div className="pending-notification-badge" data-count={totalPending}>
                        <FloatingButton
                          variant="warning"
                          size="small"
                          icon="🔔"
                          title={`${totalPending} pending item${totalPending !== 1 ? 's' : ''} — click to review`}
                          onClick={() => navigate('/teams/my-teams', { state: { focusPending: true } })}
                        >
                          <span className="btn-text">Pending ({totalPending})</span>
                        </FloatingButton>
                      </div>
                    );
                  })()}
                  <FloatingButton
                    variant="secondary"
                    size="small"
                    onClick={() => navigate('/teams/browse')}
                    icon="🔍"
                  >
                    <span className="btn-text">Browse Teams</span>
                  </FloatingButton>
                  <FloatingButton
                    variant="outline"
                    size="small"
                    onClick={() => navigate('/teams/my-teams')}
                    icon="👥"
                  >
                    <span className="btn-text">My Teams</span>
                  </FloatingButton>
                </div>
              </div>
            </FloatingCard>
          </div>

          <TeamCreator
            currentUser={currentUser}
            onTeamCreated={() => navigate('/teams/my-teams')}
            onCancel={() => navigate('/teams/browse')}
          />
        </div>
      </GameLayout>
    );
  }

  // Default view: Browse teams
  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={currentUser}
      showFloatingNav={true}
    >
      <div className="team-management-gaming">
        {/* Compact Teams Header */}
        <div className="teams-header-compact">
          <FloatingCard className="header-card-compact delay-1">
            <div className="teams-nav-compact">
              <div className="teams-nav-left-compact">
                <h2 className="teams-title-compact">🏆 Team Arena</h2>
                <p className="teams-subtitle-compact">
                  Discover, join, and manage your sports teams
                </p>
              </div>
              <div className="teams-nav-right-compact">
                {currentUser && (
                  <>
                    {/* Pending Requests Notification Bell — all sources */}
                    {(() => {
                      const totalPending = invitations.length + incomingRequests.length + tournamentTeamInvites.length;
                      return totalPending > 0 && (
                        <div className="pending-notification-badge" data-count={totalPending}>
                          <FloatingButton
                            variant="warning"
                            size="small"
                            icon="🔔"
                            title={`${totalPending} pending item${totalPending !== 1 ? 's' : ''} — click to review`}
                            onClick={() => navigate('/teams/my-teams', { state: { focusPending: true } })}
                          >
                            <span className="btn-text">Pending ({totalPending})</span>
                          </FloatingButton>
                        </div>
                      );
                    })()}

                    <FloatingButton
                      variant="secondary"
                      size="small"
                      onClick={() => navigate('/teams/my-teams')}
                      icon="👥"
                    >
                      <span className="btn-text">My Teams</span>
                    </FloatingButton>
                    <FloatingButton
                      variant="primary"
                      size="small"
                      onClick={() => navigate('/teams/create')}
                      icon="➕"
                    >
                      <span className="btn-text">Create Team</span>
                    </FloatingButton>
                  </>
                )}
              </div>
            </div>
          </FloatingCard>
        </div>

        <TeamBrowser
          currentUser={currentUser}
          onTeamSelect={handleTeamSelect}
        />
      </div>
    </GameLayout>
  );
};

export default TeamManagement;
